import { Link } from 'react-router-dom';
import type { MediaItem } from '../lib/media';
import { formatDuration } from '../lib/media';
import { SUIT_COLOR, SUIT_MARK } from '../lib/session';
import { IconPlay } from './Icon';
import LikeButton from './LikeButton';

const STATE_LABEL: Partial<Record<MediaItem['state'], string>> = {
  queued: 'Waiting',
  uploading: 'Uploading',
  processing: 'Processing',
  failed: 'Paused',
};

/**
 * Artboard 1h: a square-cropped tile with the uploader's name on a card foot
 * under the image rather than on a scrim across it. Q2 and Q3, answered — the
 * name is friendlier on the tile, and square scans faster than masonry when
 * you are thumbing through six hundred of them.
 *
 * Video tiles show a poster frame, a duration badge and a play affordance, and
 * never autoplay here (D26).
 *
 * The heart in the foot is what makes the ambient strips a liking surface and
 * not just a view — see `LikeButton`. It is why "Coming in" on Now and Schedule
 * stays the firehose rather than being filtered to featured: a new photo has to
 * be somewhere it can be seen in order to be liked into the Featured tab, and
 * filtering the top of the funnel by its own output is a loop that never
 * starts.
 */
export default function PhotoTile(
  { item, showState = false, tab }: { item: MediaItem; showState?: boolean; tab?: string },
) {
  const stateLabel = showState && item.state !== 'done' ? STATE_LABEL[item.state] : null;

  // `tab` is set by the gallery grid and left off by the ambient strips, which
  // are the firehose and belong to no tab. The viewer reads it to decide what
  // next/previous walk, so a strip correctly gives you everything.
  const to = tab ? `/gallery/${item.id}?tab=${tab}` : `/gallery/${item.id}`;

  return (
    <Link className="tile" to={to}>
      <div className={`tile-frame${item.hidden ? ' tile-hidden' : ''}`}>
        {/* A video whose poster frame the browser refused to decode has no
            thumb. A blank frame under the play badge is honest; a broken-image
            glyph in a six-hundred-tile grid is not. */}
        {item.thumb
          ? <img src={item.thumb} alt={item.caption ?? `Photo from ${item.uploaderName}`} loading="lazy" decoding="async" />
          : <span className="tile-blank" aria-label={item.caption ?? `Video from ${item.uploaderName}`} />}
        {item.kind === 'video' && (
          <>
            <span className="tile-play"><span><IconPlay size={16} /></span></span>
            {item.durationSec !== undefined && <span className="tile-dur">{formatDuration(item.durationSec)}</span>}
          </>
        )}
        {stateLabel && <span className="tile-state">{stateLabel}</span>}
        {item.hidden && <span className="tile-state" style={{ top: 'auto', bottom: 8 }}>Hidden</span>}
        {/* On the wall is a different fact from "you liked it", so it gets its
            own mark rather than being folded into the heart. Worth having on
            every tile, not just an organiser's: in All it is how a guest sees
            which of these made it to the big screen. */}
        {item.featured && <span className="tile-wall" title="On the big screen">On the wall</span>}
      </div>
      <div className="tile-foot">
        <span className="suit" style={{ color: SUIT_COLOR[item.uploaderTeam] }}>{SUIT_MARK[item.uploaderTeam]}</span>
        <span className="tile-name">{item.uploaderName}</span>
        <LikeButton item={item} />
      </div>
    </Link>
  );
}
